import { React, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import TotalModalWrapper from '../../Share/Normal/TotalModalWrapper';

import { useLanguage } from '../../../Context/LanguageContext';
import CalcDate from '../../Share/Normal/CalcDate';
import moment from 'moment';

const LockerUsingStopModal = forwardRef((props, ref) => {
  const { exitClick, saveClick, data } = props;
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const InputRef = useRef();

  useImperativeHandle(ref, () => ({
    getValue: () => {
      return InputRef.current.getValue();
    }
  }));

  useEffect(() => {
    if (data.state === 'Empty') {
      data.isAvailable = true;
      data.isUsed = false;
      data.state = t('main.locker.empty');
      data.days = '';
      data.btnText = t('main.locker.startUsage');
    }
    else if (data.state === 'Using') {
      data.isAvailable = true;
      data.isUsed = true;
      data.state = t('main.locker.inUse');
      data.days = CalcDate.getDday(data.endDate);
      data.btnText = t('main.locker.endUsage');
    }
    else if (data.state === 'Invalid') {
      data.isAvailable = false;
      data.isUsed = false;
      data.state = t('main.locker.unavailable');
      data.days = '';
      data.btnText = t('main.locker.unlock');
    }
  }, [data])

  return (
    <TotalModalWrapper>
      <ModalView $isDark={isDark}>
        <TotalWrapper>
          <TitleWrapper>
            <Title>{t('main.locker.stop.title').replace('{name}', data.userName).replace('{locker}', data.lockerName)}</Title>
            <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText} cursor='default'>{t('main.locker.stop.desc').replace('{locker}', data.lockerName)}</Text>
          </TitleWrapper>
          <ExplanationWrapper>
            <Explanation>{t('main.locker.stop.confirm').replace('{name}', data.userName)}</Explanation>
            <Explanation>{moment(data.endDate).isAfter(moment()) ? t('main.locker.stop.daysLeft').replace('{days}', data.days) : t('main.locker.stop.daysOver').replace('{days}', data.days)}</Explanation>
          </ExplanationWrapper>
          <ButtonWrapper>
            <ExitButton onClick={exitClick}>{t('main.locker.cancel')}</ExitButton>
            <SaveButton onClick={saveClick}>{t('main.locker.stop.button')}</SaveButton>
          </ButtonWrapper>
        </TotalWrapper>
      </ModalView>
    </TotalModalWrapper>
  )
})

export default LockerUsingStopModal;

const ModalView = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 380px;
  height: 230px;

  border-radius: 10px;
  box-shadow: ${props => props.$isDark ? 'none' : '0px 40px 50px rgba(0, 0, 0, 0.1)'};
  background-color: ${props => props.theme.subBackground};
`;

const TotalWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 90.33%;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 4px;
`;

const Title = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const ExplanationWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  margin-top: 30px;

  gap: 4px;
`;

const Explanation = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.subText};

  margin: 0 0 0 0;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  margin-top: 30px;

  width: 100%;

  gap: 10px;
`;

const ExitButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 62px;
  height: 40px;

  border-radius: 10px;
  background-color: ${props => props.theme.buttonFill};

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const SaveButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 100px;
  height: 40px;

  border-radius: 10px;
  background-color: ${props => props.theme.pointRed};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.white};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.pointRedHover};
  }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.cursor};
  }

  transition: all 0.3s ease-in-out;
`;
